
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { BuilderElement } from "@/types/elements";
import { configStore, documentStore, uiStore } from "@/store/store-accessor";
import InAddDragHandle from "./InAddDragHandle.vue";
import { isElementAvailable, setElementHtmlTag } from "@/lib/elements/utils";
import Icon from "@/components/UI/Icon.vue";
import ElementIcon from "@/components/UI/ElementIcon.vue";
import ElementBadge from "@/components/ElementPropertiesPanel/ElementBadge.vue";
import { getProBadge } from "@/lib/subscriptions/free-mode";
import { closeUiPanel } from "@/lib/panels/panels";

@Component({
  components: {
    ElementIcon,
    Icon,
    InAddDragHandle,
    ElementBadge
  }
})
export default class AddElement extends Vue {
  @Prop()
  element!: BuilderElement;

  @Prop({
    default: false
  })
  isSearching!: boolean;

  @Prop()
  searchQuery!: string;

  @Prop({
    default: false
  })
  isSelected!: boolean;

  @Prop({ default: false })
  showHtmlTag!: boolean;

  isElementSelected = false;

  @Watch("isSelected", { immediate: true })
  updateIsElementIsSelected() {
    this.isElementSelected = this.isSelected;
  }

  async add() {
    if (this.unavailable) return;

    closeUiPanel("add");

    documentStore.addElement({
      elementSlug: this.element.slug
    });

    if (this.shouldSetHtmlTag) {
      setElementHtmlTag(
        uiStore.activeElementId!,
        this.htmlTag,
        `DocumentAddElement:Slug:${this.element.slug}`
      );
    }
  }

  get shouldSetHtmlTag() {
    return this.showHtmlTag && this.htmlTag;
  }

  get htmlTag() {
    if (this.showHtmlTag && this.searchQuery) {
      const foundTag = [
        this.element.htmlTag.default,
        ...this.element.htmlTag.options
      ]
        .filter(Boolean)
        .find(option => option === this.searchQuery);

      if (foundTag) {
        return foundTag;
      }
    }

    return "";
  }

  get currentlyDraggingElement() {
    return uiStore.currentlyDraggingAddElementSlug === this.element.slug;
  }

  get elementName() {
    const isThereMoreThanOneElementWithTheSameName = Object.values(
      configStore.elements
    ).find(
      storeElement =>
        storeElement?.name === this.element.name &&
        storeElement.slug !== this.element.slug
    );

    const elementOrder = uiStore.panelState.elementStudioOpen
      ? " - " + this.element.order
      : "";

    if (isThereMoreThanOneElementWithTheSameName && this.isSearching) {
      return `${this.element.slug.split("\\").join(" / ")}${elementOrder}`;
    }

    return `${this.element.name}${elementOrder}`;
  }

  get elementBadge() {
    if (
      this.element.settings &&
      this.element.settings.proOnly &&
      configStore.subscriptionMode === "free"
    ) {
      return getProBadge();
    }

    if (this.element.badge) {
      return this.element.badge;
    }

    return false;
  }

  get unavailable() {
    return !isElementAvailable(this.element);
  }

  get unavailableInformation() {
    if (!this.unavailable || !this.element.nestingRule) {
      return "";
    }

    const directParentNames = this.element.nestingRule
      .restrictedToBeADirectChildOf
      ? this.element.nestingRule.restrictedToBeADirectChildOf.map(
          parentSlug => configStore.element(parentSlug).name
        )
      : [];

    const ancestorsNames = this.element.nestingRule.restrictedToBeADescendantOf
      ? this.element.nestingRule.restrictedToBeADescendantOf.map(
          parentSlug => configStore.element(parentSlug).name
        )
      : [];

    const allAncestorNames = [...directParentNames, ...ancestorsNames];
    const allAncestorNamesString = allAncestorNames.join(`", "`);
    const plural = allAncestorNames.length > 1 ? "s" : "";

    if (allAncestorNames.length) {
      return `Must be added inside a "${allAncestorNamesString}" element${plural}`;
    }

    // should never happen
    return "This element can't be added for unknown reasons";
  }
}
